import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'customer';
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  fallbackPath = '/' 
}) => {
  const { currentUser, userRole, loading } = useAuth();
  
  // Track if we're waiting for role to load (for admin routes after login)
  const [waitingForRole, setWaitingForRole] = useState(false);

  // Set up waiting for role if needed (for admin routes)
  // MUST be called before any early returns (React hooks rule)
  useEffect(() => {
    if (currentUser && userRole === null && requiredRole === 'admin') {
      setWaitingForRole(true);
      // Wait up to 5 seconds for role to load
      const timer = setTimeout(() => {
        setWaitingForRole(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setWaitingForRole(false);
    }
  }, [currentUser, userRole, requiredRole]);
  
  // Stop waiting if role loads
  // MUST be called before any early returns (React hooks rule)
  useEffect(() => {
    if (userRole !== null && waitingForRole) {
      setWaitingForRole(false);
    }
  }, [userRole, waitingForRole]);

  // Show loading spinner while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // For development mode, allow access to protected pages even without authentication
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Commented out development bypass - now requires proper authentication
  // if (isDevelopment) {
  //   console.log('Development mode: Allowing access to protected page without authentication');
  //   return <>{children}</>;
  // }

  // If no user is authenticated, redirect to auth page
  if (!currentUser) {
    return <Navigate to={fallbackPath} replace />;
  }

  // If a specific role is required and user doesn't have it, show access denied
  if (requiredRole && userRole !== requiredRole) {
    // Allow admin users to access customer pages (admin has higher privileges)
    if (userRole === 'admin' && requiredRole === 'customer') {
      console.log('Admin user accessing customer page - allowing access');
      return <>{children}</>;
    }
    
    // If userRole is null but we have a currentUser, the role might still be loading
    // Wait longer for admin role (it might be fetching from Firestore)
    // This prevents immediate redirect after login when role is still loading
    if (currentUser && userRole === null && requiredRole === 'admin' && waitingForRole) {
      // Show loading state while waiting for role
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Verifying access...</p>
          </div>
        </div>
      );
    }
    
    // For development, allow access if user is authenticated but role is missing
    if (currentUser && !userRole && requiredRole === 'customer') {
      console.log('Development mode: Allowing customer access for authenticated user without role');
      return <>{children}</>;
    }
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You need {requiredRole} privileges to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Current role: {userRole || 'None'}
          </p>
          <div className="mt-6 space-y-3">
            <button
              onClick={() => window.history.back()}
              className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 mr-3"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors duration-200"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and has the required role (if any)
  return <>{children}</>;
};

export default ProtectedRoute;
